import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from '../lib/api.js';

export type AdminCourse = {
  id: string;
  providerId: string;
  providerName: string;
  providerShortName: string | null;
  name: string;
  cricosCode: string | null;
  category: 'vocational' | 'higher_education' | 'research' | 'trade';
  subCategory: string;
  aqfLevel: string | null;
  durationText: string | null;
  durationMonths: number | null;
  campusLocations: string[];
  intakeMonths: number[];
  annualTuitionCents: number | null;
  totalProgramCents: number | null;
  oshcAnnualCents: number | null;
  applicationFeeCents: number | null;
  materialFeesCents: number | null;
  ieltsOverall: string | null;
  ieltsMinBand: string | null;
  ieltsRequirementText: string | null;
  pteOverall: number | null;
  pteMinSkill: number | null;
  pteRequirementText: string | null;
  toeflOverall: number | null;
  toeflRequirementText: string | null;
  otherEnglishText: string | null;
  academicPrerequisites: string | null;
  courseStructure: string | null;
  careerOutcomesSummary: string | null;
  pathways: string | null;
  anzscoCodes: string[];
  visaSubclassRelevance: string[];
  documentsRequired: string[];
  scholarshipsAvailable: boolean | null;
  scholarshipsText: string | null;
  curationState: 'shell' | 'curated' | 'needs_review' | 'archived';
  lastVerifiedAt: string | null;
  // Human verification (Brief BUILD-VERIFIED) — set only via the verify
  // endpoint; verifiedByName is joined on the detail endpoint only.
  verifiedAt: string | null;
  verifiedBy: string | null;
  verifiedByName?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CreateCoursePayload = Partial<
  Omit<
    AdminCourse,
    | 'id'
    | 'providerName'
    | 'providerShortName'
    | 'createdAt'
    | 'updatedAt'
    | 'lastVerifiedAt'
    | 'verifiedAt'
    | 'verifiedBy'
    | 'verifiedByName'
  >
> & {
  providerId: string;
  name: string;
  category: AdminCourse['category'];
  subCategory: string;
};

export function useAdminCourses(filters: { category?: string; q?: string }) {
  return useQuery({
    queryKey: ['admin', 'courses', filters.category ?? 'all', filters.q ?? ''],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.category) params.set('category', filters.category);
      if (filters.q) params.set('q', filters.q);
      const qs = params.toString();
      return apiGet<{ items: AdminCourse[] }>(`/admin/courses${qs ? `?${qs}` : ''}`);
    },
    staleTime: 30_000,
  });
}

export function useAdminCourse(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'courses', 'detail', id],
    queryFn: () => apiGet<{ course: AdminCourse }>(`/admin/courses/${id}`),
    enabled: !!id,
  });
}

export function useCreateCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCoursePayload) =>
      apiPost<{ course: AdminCourse }>(`/admin/courses`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'courses'] });
      qc.invalidateQueries({ queryKey: ['courses'] }); // consumer-facing list too
    },
  });
}

export function useUpdateCourse(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<CreateCoursePayload>) =>
      apiPatch<{ course: AdminCourse }>(`/admin/courses/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'courses'] });
      qc.invalidateQueries({ queryKey: ['courses'] });
    },
  });
}

/** Mark a course as verified. The server stamps the session actor — no body. */
export function useVerifyCourse(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ course: AdminCourse }>(`/admin/courses/${id}/verify`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'courses'] });
      qc.invalidateQueries({ queryKey: ['courses'] });
    },
  });
}

/** Remove the verification stamp from a course. */
export function useUnverifyCourse(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete<{ course: AdminCourse }>(`/admin/courses/${id}/verify`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'courses'] });
      qc.invalidateQueries({ queryKey: ['courses'] });
    },
  });
}

export function useDeleteCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete<void>(`/admin/courses/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'courses'] });
      qc.invalidateQueries({ queryKey: ['courses'] });
    },
  });
}
